import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { WhopCheckoutEmbed } from "@whop/checkout/react";
import { toast } from "sonner";
import { Check, ArrowLeft, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

export default function Checkout() {
  const { packageId } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const p = t.packages;

  const [pkg, setPkg] = useState(null);
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", service_date: "" });
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Checkout — XCARET MEXICO";
    api.get("/packages").then(({ data }) => {
      const found = data.find((x) => x.id === packageId);
      if (!found) { navigate("/"); return; }
      setPkg(found);
    }).catch(() => navigate("/"));
  }, [packageId]);

  const field = (key) => (pkg ? (lang === "en" ? pkg[`${key}_en`] || pkg[key] : pkg[key]) : "");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const startPayment = async () => {
    if (!form.customer_name || !form.customer_email || !form.customer_phone) {
      toast.error(p.required);
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout", { package_id: pkg.id, ...form });
      if (data.plan_id) setSession(data);
      else toast.error(p.error);
    } catch (e) {
      toast.error(p.error);
    } finally {
      setLoading(false);
    }
  };

  if (!pkg) {
    return <div className="min-h-screen bg-jungle flex items-center justify-center"><Loader2 className="animate-spin text-teal" size={32} /></div>;
  }

  const inputCls = "bg-white rounded-none border-border mb-3";
  const returnUrl = `${window.location.origin}/compra-exitosa`;

  return (
    <div className="min-h-screen bg-sand">
      <header className="border-b border-border px-6 md:px-12 py-4 flex items-center justify-between bg-sand">
        <Link to="/" data-testid="checkout-logo-home"><Logo /></Link>
        <Link to="/#packages" className="inline-flex items-center gap-2 font-body text-sm text-jungle hover:text-teal transition-colors" data-testid="checkout-back">
          <ArrowLeft size={15} /> {p.back}
        </Link>
      </header>

      <div className={session ? "max-w-xl mx-auto px-6 md:px-12 py-10 md:py-16" : "max-w-6xl mx-auto px-6 md:px-12 py-10 md:py-16 grid lg:grid-cols-2 gap-10"}>
        {/* Summary */}
        {!session ? (
          <div>
            <p className="font-body text-xs tracking-[0.25em] uppercase text-teal font-bold mb-4">{p.summary}</p>
            <div className="bg-white border border-border overflow-hidden">
              <div className="relative h-56 overflow-hidden">
                <img src={pkg.image} alt={field("name")} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-jungle/80 to-transparent" />
                <div className="absolute bottom-5 left-6">
                  <span className="font-body text-[10px] uppercase tracking-[0.2em] text-teal font-bold block">{field("duration")}</span>
                  <h1 className="font-heading text-3xl font-semibold text-sand">{field("name")}</h1>
                </div>
              </div>
              <div className="p-6">
                <p className="font-body text-sm text-jungle-light/80 mb-5">{field("subtitle")}</p>
                <p className="font-body text-[11px] uppercase tracking-wider text-jungle-light/50 mb-2">{p.includes}</p>
                <ul className="space-y-2 mb-6">
                  {field("includes").map((inc, k) => (
                    <li key={k} className="flex items-start gap-2 font-body text-sm text-jungle">
                      <Check size={15} className="text-teal mt-0.5 shrink-0" /> {inc}
                    </li>
                  ))}
                </ul>
                <div className="border-t border-border pt-5 flex items-end justify-between">
                  <span className="font-body text-sm text-jungle-light/60">Total</span>
                  <span className="font-heading text-4xl font-semibold text-jungle">${pkg.price.toLocaleString()}<span className="text-sm font-body text-jungle-light/60 ml-1">USD</span></span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-white border border-border p-4 mb-6 flex items-center gap-4">
            <img src={pkg.image} alt={field("name")} className="w-16 h-16 object-cover shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-heading text-lg font-semibold text-jungle truncate">{field("name")}</div>
              <div className="font-body text-xs text-jungle-light/60">{field("duration")}</div>
            </div>
            <div className="font-heading text-2xl font-semibold text-teal whitespace-nowrap">${pkg.price.toLocaleString()}<span className="text-xs font-body text-jungle-light/60 ml-1">USD</span></div>
          </div>
        )}

        {/* Form / Embedded payment */}
        <div>
          {!session && <p className="font-body text-xs tracking-[0.25em] uppercase text-teal font-bold mb-4">{p.checkoutTitle}</p>}
          {!session ? (
            <div className="bg-white border border-border p-6">
              <h2 className="font-heading text-2xl font-semibold text-jungle mb-5">{p.yourData}</h2>
              <Input data-testid="checkout-name" placeholder={p.name} value={form.customer_name} onChange={set("customer_name")} className={inputCls} />
              <Input data-testid="checkout-email" type="email" placeholder={p.email} value={form.customer_email} onChange={set("customer_email")} className={inputCls} />
              <Input data-testid="checkout-phone" placeholder={p.phone} value={form.customer_phone} onChange={set("customer_phone")} className={inputCls} />
              <label className="font-body text-xs uppercase tracking-wider text-jungle-light/60 mb-1.5 block mt-1">{p.date}</label>
              <Input data-testid="checkout-date" type="date" value={form.service_date} onChange={set("service_date")} className={inputCls} />
              <button
                onClick={startPayment}
                disabled={loading}
                data-testid="checkout-continue"
                className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-teal text-sand hover:bg-teal-dark font-body py-4 transition-colors disabled:opacity-60"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowRight size={18} />}
                {loading ? p.paying : p.continue}
              </button>
              <p className="flex items-center justify-center gap-1.5 font-body text-xs text-jungle-light/60 mt-4">
                <ShieldCheck size={13} className="text-teal" /> {p.secure}
              </p>
            </div>
          ) : (
            <div className="bg-white border border-border p-2 md:p-4 w-full" data-testid="whop-embed-container">
              <WhopCheckoutEmbed
                planId={session.plan_id}
                sessionId={session.checkout_id}
                prefill={{ email: form.customer_email, name: form.customer_name, phone: form.customer_phone, address: { name: form.customer_name } }}
                returnUrl={returnUrl}
                theme="light"
                fallback={
                  <div className="flex flex-col items-center justify-center py-20 gap-3">
                    <Loader2 className="animate-spin text-teal" size={28} />
                    <span className="font-body text-sm text-jungle-light/70">{p.loadingPay}</span>
                  </div>
                }
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
